import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
    TextField, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Divider,
    IconButton, Paper, Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, applyLeaveBlank, getBlankQuotesForPlan, BlankQuoteNotice } from './leaveBlank';

export const MANUAL_PREMIUM_PLAN_ID = 'manual_premium';

const CURRENCIES = ['USD', 'EURO', 'GBP', 'SGD', 'AUD', 'PHP'];
const OTHER_CURRENCY = 'Other';const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];

// Same compact sizing as the Compact_Rates_Dialog selects.
const INPUT_SX = { '& .MuiInputBase-root': { fontSize: 12, height: 32, backgroundColor: '#fff' } };
const HEAD_CELL_SX = { color: '#fff', fontWeight: 600, fontSize: 13 };

function Field({ label, required, sx, children }) {
    return (
        <Box sx={sx}>
            <Typography fontWeight={600} sx={{ fontSize: 13, mb: 0.5 }}>
                {label}{required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
            </Typography>
            {children}
        </Box>
    );
}

function emptyClientForm() {
    return { currency: 'USD', other_currency: '', payment_frequency: 'Annually', premium_rate: '' };
}

// A currency outside the list is shown as "Other" with the code in its own field.
function getCurrency(clientForm) {
    return clientForm.currency === OTHER_CURRENCY ? clientForm.other_currency.trim() : clientForm.currency;
}

function findEditingPremium(quotations, quoteUid, clientUid, editingPlan) {
    if (!editingPlan) return null;
    const quotation = quotations.find(q => q.uid === quoteUid);
    const cp = quotation?.client_premiums?.find(c => c.client_uid === clientUid);
    return cp?.premiums?.find(p => p.plan_uid === editingPlan.uid) || null;
}

// One form per quote: the plan details are entered per quote, the premium per client.
function buildInitialForms(quotes, clients, quotations, editingPlan) {
    return Object.fromEntries(quotes.map(quote => {
        const findPremium = clientUid => findEditingPremium(quotations, quote.uid, clientUid, editingPlan);
        const firstPremium = clients.map(c => findPremium(c.uid)).find(Boolean);

        return [quote.uid, {
            insurer: firstPremium?.insurer || '',
            plan: firstPremium?.plan_label || firstPremium?.plan || '',
            included_benefits: firstPremium?.included_benefits || '',
            deductible: firstPremium?.deductible || '',
            area_of_coverage: firstPremium?.area_of_coverage || '',
            clients: Object.fromEntries(clients.map(client => {
                const premium = findPremium(client.uid);
                if (!premium) return [client.uid, emptyClientForm()];
                const currency = premium.currency || 'USD';
                const isListed = CURRENCIES.includes(currency);
                return [client.uid, {
                    uid: premium.uid || '',
                    currency: isListed ? currency : OTHER_CURRENCY,
                    other_currency: isListed ? '' : currency,
                    payment_frequency: premium.payment_frequency || 'Annually',
                    premium_rate: premium.premium_rate ?? '',
                }];
            })),
        }];
    }));
}

// Blank quotes skip validation, but at least one quote must be filled in to name the plan.
function isFormComplete(forms, quotes, clients, blankQuotes) {
    const filledQuotes = quotes.filter(quote => !blankQuotes[quote.uid]);
    return filledQuotes.length > 0 && filledQuotes.every(quote => {
        const form = forms[quote.uid];
        return form
            && form.insurer.trim() && form.plan.trim() && form.deductible.trim() && form.area_of_coverage.trim()
            && clients.every(client => {
                const c = form.clients[client.uid];
                return c && getCurrency(c) && c.payment_frequency
                    && c.premium_rate !== '' && !isNaN(Number(c.premium_rate));
            });
    });
}

export default function ManualPremiumFormDialog({ open, onClose, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null }) {
    const [forms, setForms] = useState({});
    const [blankQuotes, setBlankQuotes] = useState({});

    useEffect(() => {
        if (open) {
            setForms(buildInitialForms(quotes, clients, quotations, editingPlan));
            setBlankQuotes(getBlankQuotesForPlan(quotes, quotations, editingPlan));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Clients are refreshed from the input table as the dialog opens, so fill in any
    // quote or client that arrived after the forms were built, keeping what was typed.
    useEffect(() => {
        if (!open) return;
        setForms(prev => {
            const initial = buildInitialForms(quotes, clients, quotations, editingPlan);
            return Object.fromEntries(Object.entries(initial).map(([quoteUid, form]) => {
                const existing = prev[quoteUid];
                if (!existing) return [quoteUid, form];
                return [quoteUid, { ...existing, clients: { ...form.clients, ...existing.clients } }];
            }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotes, clients]);

    function updateQuoteField(quoteUid, field, value) {
        setForms(prev => ({ ...prev, [quoteUid]: { ...prev[quoteUid], [field]: value } }));
    }

    // When the first quote's field is left, copy it into the other quotes that are still empty.
    // Runs on blur rather than per keystroke, which would copy only the first character.
    function copyFromFirstQuote(field) {
        const firstUid = quotes[0]?.uid;
        setForms(prev => {
            const value = prev[firstUid]?.[field];
            if (!value?.trim()) return prev;
            const next = { ...prev };
            quotes.slice(1).forEach(quote => {
                if (next[quote.uid] && next[quote.uid][field] === '') {
                    next[quote.uid] = { ...next[quote.uid], [field]: value };
                }
            });
            return next;
        });
    }

    function updateClientField(quoteUid, clientUid, field, value) {
        setForms(prev => ({
            ...prev,
            [quoteUid]: {
                ...prev[quoteUid],
                clients: {
                    ...prev[quoteUid].clients,
                    [clientUid]: { ...prev[quoteUid].clients[clientUid], [field]: value },
                },
            },
        }));
    }

    function handleSubmit() {
        if (!onAddToQuotations) return;
        // plan_id is shared by every manual premium; uid tells the instances apart.
        // Editing reuses the existing plan's uid so it replaces that plan in place.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        quotes.forEach(quote => {
            const form = forms[quote.uid];
            const insurer = form.insurer.trim();
            const planName = form.plan.trim();
            clients.forEach(client => {
                const clientForm = form.clients[client.uid];
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: MANUAL_PREMIUM_PLAN_ID,
                    planUid,
                    premium: {
                        uid: clientForm.uid || uuidv4(),
                        plan_id: MANUAL_PREMIUM_PLAN_ID,
                        plan_uid: planUid,
                        insurer,
                        plan: planName,
                        plan_label: planName,
                        included_benefits: form.included_benefits.trim(),
                        deductible: form.deductible.trim(),
                        area_of_coverage: form.area_of_coverage.trim(),
                        currency: getCurrency(clientForm),
                        payment_frequency: clientForm.payment_frequency,
                        premium_rate: clientForm.premium_rate === '' ? null : Number(clientForm.premium_rate),
                        discount: '0',
                        loading: '0',
                        blank: false,
                    },
                });
            });
        });

        const blankQuoteUids = quotes.filter(quote => blankQuotes[quote.uid]).map(quote => quote.uid);
        const firstFilledQuote = quotes.find(quote => !blankQuotes[quote.uid]);
        const firstForm = forms[firstFilledQuote?.uid];
        const firstClientForm = firstForm && clients[0] && firstForm.clients[clients[0].uid];
        // The plan chip shows the first filled-in quote's insurer and plan.
        const insurer = firstForm?.insurer.trim() || '';
        const planName = firstForm?.plan.trim() || '';
        const plan = {
            plan_id: MANUAL_PREMIUM_PLAN_ID,
            uid: planUid,
            plan: planName,
            insurer,
            label: `${insurer} ${planName}`.trim(),
            payloads: {
                area_of_coverage: firstForm?.area_of_coverage.trim() || '',
                currency: firstClientForm ? getCurrency(firstClientForm) : '',
                payment_frequency: firstClientForm?.payment_frequency || '',
            },
            deductibles_per_quote: Object.fromEntries(
                quotes.map(quote => [quote.uid, blankQuotes[quote.uid] ? LEAVE_BLANK : forms[quote.uid].deductible.trim()])
            ),
        };

        onAddToQuotations(applyLeaveBlank(updates, blankQuoteUids), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const isReady = quotes.length > 0 && clients.length > 0 && isFormComplete(forms, quotes, clients, blankQuotes);

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    backgroundColor: '#f1f4fa',
                    maxHeight: '90vh',
                },
            }}
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5 }}>
                <Typography fontWeight={600} sx={{ fontSize: 18 }}>
                    Manual Premium
                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 14 }}>
                        (Enter premium manually)
                    </Typography>
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ mr: -1 }}>
                    <CloseIcon fontSize="medium" />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {quotes.map((quote, quoteIndex) => {
                    const form = forms[quote.uid];
                    if (!form) return null;
                    return (
                        <Box key={quote.uid}>
                            {quoteIndex > 0 && <Divider sx={{ my: 2.5 }} />}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, mt: quoteIndex === 0 ? 1 : 0 }}>
                                <Typography fontWeight={600} sx={{ fontSize: 15 }}>
                                    {quote.name}
                                </Typography>
                                <FormControlLabel
                                    sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!!blankQuotes[quote.uid]}
                                            onChange={e => setBlankQuotes(prev => ({ ...prev, [quote.uid]: e.target.checked }))}
                                        />
                                    }
                                    label={LEAVE_BLANK}
                                />
                            </Box>

                            {blankQuotes[quote.uid] ? <BlankQuoteNotice /> : (<>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 1.5 }}>
                                <Field label="Insurer" required>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={form.insurer}
                                        onChange={e => updateQuoteField(quote.uid, 'insurer', e.target.value)}
                                        onBlur={quoteIndex === 0 ? () => copyFromFirstQuote('insurer') : undefined}
                                        sx={INPUT_SX}
                                    />
                                </Field>
                                <Field label="Plan" required>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={form.plan}
                                        onChange={e => updateQuoteField(quote.uid, 'plan', e.target.value)}
                                        onBlur={quoteIndex === 0 ? () => copyFromFirstQuote('plan') : undefined}
                                        sx={INPUT_SX}
                                    />
                                </Field>
                                <Field label="Deductible" required>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={form.deductible}
                                        onChange={e => updateQuoteField(quote.uid, 'deductible', e.target.value)}
                                        sx={INPUT_SX}
                                    />
                                </Field>
                                <Field label="Area of Cover" required>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={form.area_of_coverage}
                                        onChange={e => updateQuoteField(quote.uid, 'area_of_coverage', e.target.value)}
                                        onBlur={quoteIndex === 0 ? () => copyFromFirstQuote('area_of_coverage') : undefined}
                                        sx={INPUT_SX}
                                    />
                                </Field>
                                <Field label="Included Benefits (Optional, eg. Inpatient + Outpatient)">
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={form.included_benefits}
                                        onChange={e => updateQuoteField(quote.uid, 'included_benefits', e.target.value)}
                                        sx={INPUT_SX}
                                    />
                                </Field>
                            </Box>

                            <Paper variant="outlined" sx={{ mt: 2, overflowX: 'auto', backgroundColor: '#fff' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#0c1b4e' }}>
                                            <TableCell sx={HEAD_CELL_SX}>Client</TableCell>
                                            <TableCell sx={HEAD_CELL_SX}>Currency</TableCell>
                                            <TableCell sx={HEAD_CELL_SX}>Payment Frequency</TableCell>
                                            <TableCell sx={HEAD_CELL_SX}>Premium Rate</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clients.map((client, clientIndex) => {
                                            const clientForm = form.clients[client.uid];
                                            if (!clientForm) return null;
                                            return (
                                                <TableRow key={client.uid}>
                                                    <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>
                                                        {[
                                                            client.full_name || `Client ${clientIndex + 1}`,
                                                            client.age,
                                                            client.country_of_residence,
                                                        ].filter(value => value !== undefined && value !== null && value !== '').join(', ')}
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 130 }}>
                                                        <TextField
                                                            select
                                                            size="small"
                                                            fullWidth
                                                            value={clientForm.currency}
                                                            onChange={e => updateClientField(quote.uid, client.uid, 'currency', e.target.value)}
                                                            sx={INPUT_SX}
                                                        >
                                                            {CURRENCIES.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
                                                            <MenuItem value={OTHER_CURRENCY} sx={{ fontSize: 12 }}>{OTHER_CURRENCY}</MenuItem>
                                                        </TextField>
                                                        {clientForm.currency === OTHER_CURRENCY && (
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                placeholder="e.g. HKD"
                                                                value={clientForm.other_currency}
                                                                onChange={e => updateClientField(quote.uid, client.uid, 'other_currency', e.target.value.toUpperCase())}
                                                                sx={{ ...INPUT_SX, mt: 1 }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 150 }}>
                                                        <TextField
                                                            select
                                                            size="small"
                                                            fullWidth
                                                            value={clientForm.payment_frequency}
                                                            onChange={e => updateClientField(quote.uid, client.uid, 'payment_frequency', e.target.value)}
                                                            sx={INPUT_SX}
                                                        >
                                                            {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>)}
                                                        </TextField>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 140 }}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            inputProps={{ inputMode: 'decimal' }}
                                                            value={clientForm.premium_rate}
                                                            onChange={e => {
                                                                // A text input avoids the number spinner; only digits and one decimal point get through.
                                                                if (/^\d*\.?\d*$/.test(e.target.value)) {
                                                                    updateClientField(quote.uid, client.uid, 'premium_rate', e.target.value);
                                                                }
                                                            }}
                                                            sx={INPUT_SX}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                            </>)}
                        </Box>
                    );
                })}
                {clients.length === 0 && (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 2 }}>
                        Add at least one client before entering a manual premium.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    disabled={!isReady}
                    onClick={handleSubmit}
                    sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >
                    {editingPlan ? 'Update Premiums' : 'Add Premiums'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
